/* @jsx h */
import { getDigitalReceiptUrl } from "@app/utils/digitalReceipt";
import { LemmonModal, QRCodeComponent } from "@lemmonhq/uikit";
import { computed, defineComponent, h, ref } from "vue";
import { useToast } from "../../providers";
import { useTrpc } from "../../providers/TrpcProvider";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "../../providers/Auth";

type DigitalReceiptModalProps = {
  paymentId: number;
  paymentGuid: string;
  total: number;
  onClose: () => void;
};

export const DigitalReceiptModal = defineComponent<DigitalReceiptModalProps>({
  name: "DigitalReceiptModal",
  props: ["paymentId", "paymentGuid", "total", "onClose"],
  setup(props) {
    const trpc = useTrpc();
    const toast = useToast();
    const auth = useAuth();
    const { t } = useLocalization();
    const email = ref("");
    const emailError = ref("");
    const isSending = ref(false);

    const receiptUrl = computed(() =>
      getDigitalReceiptUrl(
        import.meta.env.VITE_CUSTOMER_APP_PUBLIC_URL,
        props.paymentGuid,
      ),
    );

    const validateEmail = () => {
      const value = email.value.trim();

      if (!value) {
        emailError.value = t("reports.tx.digital_receipt.email_required");
        return false;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        emailError.value = t("reports.tx.digital_receipt.email_invalid");
        return false;
      }

      emailError.value = "";
      return true;
    };

    const copyReceiptUrl = async () => {
      try {
        await navigator.clipboard.writeText(receiptUrl.value);
        toast.showToast(t("reports.tx.digital_receipt.link_copied"), {
          type: "success",
          icon: "content_copy",
        });
      } catch (error) {
        console.error(error);
        toast.showToast(t("reports.tx.digital_receipt.link_copy_failed"), {
          type: "error",
        });
      }
    };

    const handleSubmit = async (event?: Event) => {
      event?.preventDefault();

      if (isSending.value || !validateEmail()) {
        return;
      }

      isSending.value = true;

      try {
        await trpc.payments.sendDigitalReceipt.mutate({
          paymentId: props.paymentId,
          email: email.value.trim(),
        });
        toast.showToast(t("reports.tx.digital_receipt.send_success"), {
          type: "success",
          icon: "mail",
        });
        props.onClose();
      } catch (error: any) {
        console.error(error);
        toast.showToast(
          error?.message || t("reports.tx.digital_receipt.send_failed"),
          {
            type: "error",
          },
        );
      } finally {
        isSending.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={t("reports.tx.digital_receipt.title")}
        onClose={props.onClose}
        style="max-width: 840px;"
        disableCloseByClickingBackdrop={isSending.value}
        renderButtons={() => (
          <div class="border-t border-base-300 bg-base-100/95 px-4 py-4 sm:px-6">
            <div class="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                class="btn btn-ghost"
                disabled={isSending.value}
                onClick={() => props.onClose()}
              >
                {t("general.words.cancel")}
              </button>
              <button
                type="submit"
                form="digital-receipt-form"
                class={`btn btn-primary ${
                  isSending.value ? "btn-disabled" : ""
                }`}
                disabled={isSending.value}
              >
                {isSending.value ? (
                  <span class="inline-flex items-center gap-2">
                    <span class="loading loading-spinner loading-xs"></span>
                    {t("reports.tx.digital_receipt.sending_button")}
                  </span>
                ) : (
                  t("reports.tx.digital_receipt.send_button")
                )}
              </button>
            </div>
          </div>
        )}
      >
        <div class="bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.28),_transparent_30%),linear-gradient(180deg,rgba(28,35,52,0.05),rgba(28,35,52,0))]">
          <div class="grid gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
            <div class="border-b border-base-300 bg-base-200/35 p-4 sm:p-5 lg:border-b-0 lg:border-r">
              <div class="rounded-[24px] border border-base-300 bg-base-100 p-4 shadow-sm">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="text-[11px] uppercase tracking-[0.22em] text-accent">
                      {t("reports.tx.digital_receipt.qr_label")}
                    </div>
                  </div>
                  <div class="rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
                    {auth.user.value?.restaurant.currency || "CHF"}{" "}
                    {props.total.toFixed(2)}
                  </div>
                </div>

                <div class="mt-4 rounded-[22px] bg-white p-3 shadow-[0_14px_36px_rgba(15,23,42,0.10)]">
                  <QRCodeComponent url={receiptUrl.value} />
                </div>

                <div class="mt-4 rounded-2xl border border-base-300 bg-base-100/80 p-3">
                  <div class="mb-2 flex items-center justify-between gap-2">
                    <span class="text-[11px] font-medium uppercase tracking-[0.16em] text-base-content/45">
                      {t("reports.tx.digital_receipt.link_label")}
                    </span>
                    <button
                      type="button"
                      class="btn btn-ghost btn-xs gap-2"
                      onClick={copyReceiptUrl}
                    >
                      <span class="material-symbols-outlined text-base">
                        content_copy
                      </span>
                      {t("reports.tx.digital_receipt.copy_button")}
                    </button>
                  </div>
                  <div class="rounded-xl bg-base-200/70 px-3 py-2 font-mono text-xs leading-5 text-base-content/75 break-all">
                    {receiptUrl.value}
                  </div>
                </div>
              </div>
            </div>

            <div class="p-4 sm:p-5">
              <form
                id="digital-receipt-form"
                class="flex h-full flex-col"
                onSubmit={handleSubmit}
              >
                <div class="rounded-[24px] border border-base-300 bg-base-100 p-4 shadow-sm sm:p-5">
                  <div class="text-[11px] uppercase tracking-[0.22em] text-base-content/45">
                    {t("reports.tx.digital_receipt.delivery_label")}
                  </div>
                  <h3 class="mt-2 text-2xl font-semibold text-accent">
                    {t("reports.tx.digital_receipt.delivery_title")}
                  </h3>
                  <div class="mt-5">
                    <label
                      for="digital-receipt-email"
                      class="mb-2 block text-sm font-medium text-base-content/80"
                    >
                      {t("reports.tx.digital_receipt.email_label")}
                    </label>
                    <input
                      id="digital-receipt-email"
                      type="email"
                      value={email.value}
                      onInput={(event) => {
                        email.value = (event.target as HTMLInputElement).value;
                        if (emailError.value) {
                          validateEmail();
                        }
                      }}
                      placeholder={t(
                        "reports.tx.digital_receipt.email_placeholder",
                      )}
                      class={`input input-bordered h-14 w-full rounded-2xl bg-base-100 text-base ${
                        emailError.value ? "input-error" : ""
                      }`}
                      autocomplete="email"
                      disabled={isSending.value}
                    />
                    <div class="mt-2 min-h-6 text-sm">
                      {emailError.value ? (
                        <span class="text-error">{emailError.value}</span>
                      ) : (
                        <span class="text-base-content/55">
                          {t("reports.tx.digital_receipt.email_hint")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </LemmonModal>
    );
  },
});
